import { useQuery } from '@tanstack/react-query';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import api from '../api/config';
import { useUser } from './UserProvider';

type ChatStatusEntry = {
    unread: boolean;
    team: string | null;
    lastMessageText: string | null;
    lastMessageDate: string | null;
    reviewedBy: string | null;
};

interface ProfilePictureEntry {
    url: string;
    thumbUrl: string | null;
}

interface PatientMetadataContextType {
    getProfilePictureUrl: (patientId: string | null | undefined) => string | undefined;
    getProfileThumbnailUrl: (patientId: string | null | undefined) => string | null | undefined;
    hasUnreadMessages: (patientId: string | null | undefined) => boolean;
    getLastMessage: (patientId: string) => { text: string | null; date: string | null } | undefined;
    getEntry: (patientId: string) => ChatStatusEntry | undefined;
    unreadPatientIds: Set<string>;
    allConversationIds: Set<string>;
    isLoading: boolean;
    isFetching: boolean;
    refresh: () => Promise<void>;
}

const PatientMetadataContext = createContext<PatientMetadataContextType | undefined>(undefined);

export function usePatientMetadata() {
    const context = useContext(PatientMetadataContext);
    if (context === undefined) {
        throw new Error('usePatientMetadata must be used within PatientMetadataProvider');
    }
    return context;
}

interface PatientMetadataProviderProps {
    children: ReactNode;
}

export function PatientMetadataProvider({ children }: PatientMetadataProviderProps) {
    // Gate the queries on the user being ready. Without this, queries fire from
    // the /login page with no token, silently cache the 401 response under keys
    // that never get invalidated, and the Messages page shows empty until a
    // full page refresh remounts the provider. Including `practiceIds` in the
    // queryKey ensures the cache re-fetches when the user's assignments change
    // (and prevents cross-user pollution on shared browsers).
    const { user } = useUser();
    const practiceIds = user?.database?.data?.practices;
    const isUserReady = !!practiceIds?.length;

    const {
        data: profilePictures = {},
        isLoading: isPicturesLoading,
        refetch: refetchPictures
    } = useQuery({
        queryKey: ['profile-pictures', practiceIds],
        queryFn: async (): Promise<Record<string, ProfilePictureEntry>> => {
            const response = await api.get<Record<string, string | ProfilePictureEntry>>(
                '/patients/profile-pictures'
            );
            const result: Record<string, ProfilePictureEntry> = {};
            for (const [id, value] of Object.entries(response.data)) {
                if (typeof value === 'string') {
                    result[id] = { url: value, thumbUrl: null };
                } else {
                    result[id] = value;
                }
            }
            return result;
        },
        enabled: isUserReady,
        staleTime: 60 * 60 * 1000,
        refetchInterval: 60 * 60 * 1000
    });

    const {
        data: chatStatuses = {},
        isLoading: isUnreadLoading,
        isFetching: isUnreadFetching,
        refetch: refetchUnread
    } = useQuery({
        queryKey: ['unread-messages', practiceIds],
        queryFn: async (): Promise<Record<string, ChatStatusEntry>> => {
            const response = await api.get<Record<string, any>>('/patients/unread-messages', {
                params: { viewerType: 'practice', filter: 'all' }
            });
            const result: Record<string, ChatStatusEntry> = {};
            for (const [id, entry] of Object.entries(response.data)) {
                if (typeof entry === 'boolean') {
                    result[id] = {
                        unread: entry,
                        team: null,
                        lastMessageText: null,
                        lastMessageDate: null,
                        reviewedBy: null
                    };
                } else {
                    result[id] = entry;
                }
            }
            return result;
        },
        enabled: isUserReady,
        staleTime: 15 * 60 * 1000,
        refetchInterval: 15 * 60 * 1000,
        refetchOnMount: 'always'
    });

    const getProfilePictureUrl = useCallback(
        (patientId: string | null | undefined): string | undefined => {
            if (!patientId) return undefined;
            return profilePictures[patientId]?.url;
        },
        [profilePictures]
    );

    const getProfileThumbnailUrl = useCallback(
        (patientId: string | null | undefined): string | null | undefined => {
            if (!patientId) return undefined;
            return profilePictures[patientId]?.thumbUrl;
        },
        [profilePictures]
    );

    const hasUnreadMessages = useCallback(
        (patientId: string | null | undefined): boolean => {
            if (!patientId) return false;
            return chatStatuses[patientId]?.unread ?? false;
        },
        [chatStatuses]
    );

    const getLastMessage = useCallback(
        (patientId: string): { text: string | null; date: string | null } | undefined => {
            const entry = chatStatuses[patientId];
            if (!entry) return undefined;
            return { text: entry.lastMessageText, date: entry.lastMessageDate };
        },
        [chatStatuses]
    );

    const getEntry = useCallback(
        (patientId: string): ChatStatusEntry | undefined => chatStatuses[patientId],
        [chatStatuses]
    );

    const unreadPatientIds = useMemo(() => {
        const ids = new Set<string>();
        for (const [id, entry] of Object.entries(chatStatuses)) {
            if (entry.unread) ids.add(id);
        }
        return ids;
    }, [chatStatuses]);

    const allConversationIds = useMemo(() => new Set(Object.keys(chatStatuses)), [chatStatuses]);

    const isLoading = isPicturesLoading || isUnreadLoading;
    const isFetching = isUnreadFetching;

    const refresh = useCallback(async () => {
        await Promise.all([refetchPictures(), refetchUnread()]);
    }, [refetchPictures, refetchUnread]);

    const value = useMemo(
        () => ({
            getProfilePictureUrl,
            getProfileThumbnailUrl,
            hasUnreadMessages,
            getLastMessage,
            getEntry,
            unreadPatientIds,
            allConversationIds,
            isLoading,
            isFetching,
            refresh
        }),
        [
            getProfilePictureUrl,
            getProfileThumbnailUrl,
            hasUnreadMessages,
            getLastMessage,
            getEntry,
            unreadPatientIds,
            allConversationIds,
            isLoading,
            isFetching,
            refresh
        ]
    );

    return (
        <PatientMetadataContext.Provider value={value}>{children}</PatientMetadataContext.Provider>
    );
}
